/**
 * Adaptateurs "ligne d'évaluation" — Phase 3.1 (liste Évaluations unifiée).
 *
 * Trois sources hétérogènes sont normalisées vers une ligne commune AVANT le
 * rendu : la page ne voit qu'un seul format, l'hétérogénéité se règle ici
 * (même principe que toPayloadUnifie côté questions EvalNat).
 *
 * Ligne commune :
 * {
 *   cle:      'classique-12' | 'session-5' | 'bilan-8'   (clé React unique inter-sources)
 *   source:   'classique' | 'session' | 'bilan'
 *   badge:    'saisie' | 'qr_code' | 'en_ligne' | 'bilan' (pilote le badge de type)
 *   titre:    string
 *   dateISO:  'YYYY-MM-DD' | '' (tri décroissant, '' en dernier)
 *   statut:   'en_attente' | 'terminee'                   (classique — saisie faite ou non)
 *             | 'en_attente' | 'en_cours' | 'terminee'    (session)
 *             | 'non_commence' | 'en_cours' | 'termine'   (bilan, dérivé des réponses)
 *   notee:    bool — session déjà reliée à une évaluation de notes
 *   meta:     { nbQuestions?, nbSavoirFaire?, duree?, nbReponses?, nbEleves? }
 *   original: l'objet source intact (3.2 : actions / navigation)
 * }
 */

// ─── Adaptateurs par source ────────────────────────────────────────────

/** Évaluation classique (wizard "mon sujet", éval papier 2.3, devoir commun).
 *  `savoir_faires` est un OBJET de compteurs par couleur d'acquisition
 *  (nb_vert_plus…nb_no_scores) — il reste dans `original` pour la barre de
 *  répartition, rendue par la page. */
export const rowFromEvaluationClassique = (ev) => ({
    cle: `classique-${ev.id}`,
    source: 'classique',
    badge: 'saisie',
    titre: ev.titre || 'Sans titre',
    dateISO: ev.date || '',
    statut: ev.statut || null,
    notee: false,
    meta: {
        nbEleves: ev.nombre_eleves ?? null,
        moyenne: typeof ev.moyenne_scores === 'number' ? ev.moyenne_scores : null,
    },
    original: ev,
});

/** Session QCM (ArUco en classe ou en ligne).
 *  `eleves_reponses` est dans le payload de liste (SerializerMethodField) :
 *  en ligne, c'est toute la classe avec `reponses` null/rempli → on en tire
 *  le nombre de répondants côté client, sans fetch supplémentaire. */
export const rowFromSessionQCM = (s) => {
    const reponses = Array.isArray(s.eleves_reponses) ? s.eleves_reponses : [];
    const nbReponses = reponses.filter((e) => e.reponses != null).length;
    return {
        cle: `session-${s.id}`,
        source: 'session',
        badge: s.mode_passation === 'en_ligne' ? 'en_ligne' : 'qr_code',
        titre: s.titre || 'Sans titre',
        dateISO: s.date_passation || '',
        statut: s.statut || 'en_attente',
        notee: Boolean(s.evaluation),
        meta: {
            nbQuestions: Array.isArray(s.questions) ? s.questions.length : null,
            equipe: s.mode === 'equipe',
            nbReponses,
            nbEleves: reponses.length,
        },
        original: s,
    };
};

/** Bilan EvalNat — statut dérivé des réponses (même règle qu'EvalBilan). */
export const rowFromBilanEvalNat = (b) => {
    const reponses = Array.isArray(b.eleves_reponses) ? b.eleves_reponses : [];
    const nbReponses = reponses.filter((e) => e.reponses != null).length;
    const total = reponses.length;
    const statut = nbReponses === 0 ? 'non_commence'
        : (nbReponses === total && total > 0) ? 'termine' : 'en_cours';
    return {
        cle: `bilan-${b.id}`,
        source: 'bilan',
        badge: 'bilan',
        titre: b.titre || 'Sans titre',
        dateISO: b.date || '',
        statut,
        notee: false,
        meta: {
            nbQuestions: Array.isArray(b.questions) ? b.questions.length : null,
            duree: b.duree || null,
            nbReponses,
            nbEleves: total,
        },
        original: b,
    };
};

// ─── Cycle de vie d'une session QCM ─────────────────────────────────────

/**
 * Phase dérivée d'une session, plus fine que `statut` : distingue le cas
 * « programmée » (en_attente + date future, pas encore accessible) de
 * « prête à lancer », et sépare ArUco/en ligne quand c'est utile.
 *
 * @param {Object} session - objet session brut (statut, date_passation, mode_passation)
 * @param {string} todayISO - date du jour 'YYYY-MM-DD'
 * @returns {'programmee'|'prete'|'ouverte'|'en_cours'|'terminee'}
 */
export const sessionPhase = (session, todayISO) => {
    const statut = session?.statut || 'en_attente';
    if (statut === 'terminee') return 'terminee';
    if (statut === 'en_cours') return 'en_cours';
    // en_attente : programmée si une date future est posée
    const future = session?.date_passation && session.date_passation > todayISO;
    if (future) return 'programmee';
    return session?.mode_passation === 'en_ligne' ? 'ouverte' : 'prete';
};

/**
 * Phase d'un bilan EvalNat — un bilan est une passation EN LIGNE pour les
 * élèves : même langage que les sessions en ligne (programmée → ouverte →
 * en cours → terminée). Le statut n'est pas porté par le backend mais dérivé
 * des réponses (comme dans rowFromBilanEvalNat).
 *
 * @returns {'programmee'|'ouverte'|'en_cours'|'terminee'}
 */
export const bilanPhase = (bilan, todayISO) => {
    const reponses = Array.isArray(bilan?.eleves_reponses) ? bilan.eleves_reponses : [];
    const nbRep = reponses.filter((e) => e.reponses != null).length;
    const total = reponses.length;
    if (nbRep > 0 && nbRep >= total && total > 0) return 'terminee';
    if (nbRep > 0) return 'en_cours';
    // aucune réponse encore : programmée si date future, sinon ouverte aux élèves
    const future = bilan?.date && bilan.date > todayISO;
    return future ? 'programmee' : 'ouverte';
};

// ─── Règles d'inclusion ────────────────────────────────────────────────

/**
 * Bilans créés par le prof uniquement : les nationales importées et les
 * devoirs communs EvalNat vivent dans Pilotage → Résultats nationaux
 * (décision 3.1 — même règle que les "bilans" d'EvalNatLoader).
 */
export const estBilanProf = (b) => {
    const titre = b?.titre || '';
    return !titre.includes('Evaluation Nationale') && !titre.includes('Devoir Commun');
};

// ─── Agrégation ────────────────────────────────────────────────────────

/**
 * Fusionne les 3 sources (déjà filtrées classe/créateur en amont) en lignes
 * triées par date décroissante.
 *
 * Déduplication : "Noter" une session crée une évaluation classique liée
 * (champ session.evaluation). Cette éval ne doit PAS apparaître comme une
 * 2e ligne — la session porte le badge "Notée".
 */
export const buildEvaluationRows = ({ evalsClassiques = [], sessions = [], bilans = [] }) => {
    // Ids d'évals classiques issues de la notation d'une session
    const idsEvalsDeSessions = new Set(
        sessions
            .map((s) => (typeof s.evaluation === 'object' ? s.evaluation?.id : s.evaluation))
            .filter(Boolean)
    );

    const rows = [
        ...evalsClassiques
            .filter((ev) => !idsEvalsDeSessions.has(ev.id))
            .map(rowFromEvaluationClassique),
        ...sessions.map(rowFromSessionQCM),
        ...bilans.filter(estBilanProf).map(rowFromBilanEvalNat),
    ];

    // Tri date décroissante, dates absentes en dernier
    return rows.sort((a, b) => (b.dateISO || '').localeCompare(a.dateISO || ''));
};
